import { useRef, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import useEnv from '@zenbusiness/application-commons-env';
import useAsyncEffect from '@zenbusiness/application-commons-hooks/useAsyncEffect';
import { useIntl } from '@zenbusiness/application-commons-intl';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import Button from '@zenbusiness/zui/Button';
import Heading from '@zenbusiness/zui/Heading';
import PageLoading from '@zenbusiness/zui/PageLoading';
import Text from '@zenbusiness/zui/Text';

import { accountSettingsSecurity } from '#client/constants/routes';
import useDeleteMfaMethod from '#client/hooks/useDeleteMfaMethod';
import { STEP_UP_REQUIRED } from '#client/pages/AccountSettings/components/TwoStepVerification/constants';
import { clearMfaNudgeDecision } from '#client/utils/mfaNudgeStorage';
import {
  MY_ACCOUNT_NEEDS_TWO_STEP,
  MY_ACCOUNT_OPERATIONS,
  clearPendingOperation,
  deleteAuthenticationMethod,
  enrollRecoveryCode,
  exchangeCode,
  finishPasskeyEnrollment,
  listAuthenticationMethods,
  readPendingOperation,
  startPasskeyEnrollment,
} from '#client/utils/myAccount';
import { generateMfaStepUpUrl, toSameOriginPath } from '#client/utils/urls';

import * as Styled from './MyAccountCallback.style';

export const LOCALIZED_TEXT = {
  passkeyTitle: 'dashboard.myAccountCallback.passkey.title',
  passkeyBody: 'dashboard.myAccountCallback.passkey.body',
  passkeyContinue: 'dashboard.myAccountCallback.passkey.continue',
  passkeyExistsTitle: 'dashboard.myAccountCallback.passkeyExists.title',
  passkeyExistsBody: 'dashboard.myAccountCallback.passkeyExists.body',
  recoveryCodeTitle: 'dashboard.myAccountCallback.recoveryCode.title',
  recoveryCodeBody: 'dashboard.myAccountCallback.recoveryCode.body',
  copy: 'dashboard.myAccountCallback.recoveryCode.copy',
  copied: 'dashboard.myAccountCallback.recoveryCode.copied',
  done: 'dashboard.myAccountCallback.recoveryCode.done',
  failedTitle: 'dashboard.myAccountCallback.failed.title',
  failedBody: 'dashboard.myAccountCallback.failed.body',
  needsTwoStepTitle: 'dashboard.myAccountCallback.needsTwoStep.title',
  needsTwoStepBody: 'dashboard.myAccountCallback.needsTwoStep.body',
  recoveryCodeLostTitle: 'dashboard.myAccountCallback.recoveryCodeLost.title',
  recoveryCodeLostBody: 'dashboard.myAccountCallback.recoveryCodeLost.body',
  back: 'dashboard.myAccountCallback.back',
};

const STAGES = {
  working: 'working',
  confirmPasskey: 'confirmPasskey',
  passkeyExists: 'passkeyExists',
  showRecoveryCode: 'showRecoveryCode',
  recoveryCodeLost: 'recoveryCodeLost',
  failed: 'failed',
  needsTwoStep: 'needsTwoStep',
};

// Stages that only explain an outcome and lead back, as [title, body].
const MESSAGE_STAGES = {
  [STAGES.failed]: [LOCALIZED_TEXT.failedTitle, LOCALIZED_TEXT.failedBody],
  [STAGES.needsTwoStep]: [LOCALIZED_TEXT.needsTwoStepTitle, LOCALIZED_TEXT.needsTwoStepBody],
  [STAGES.passkeyExists]: [LOCALIZED_TEXT.passkeyExistsTitle, LOCALIZED_TEXT.passkeyExistsBody],
  [STAGES.recoveryCodeLost]: [LOCALIZED_TEXT.recoveryCodeLostTitle, LOCALIZED_TEXT.recoveryCodeLostBody],
};

// OAuth error codes are lowercase words. Anything else in the link didn't come from Auth0, so it
// isn't copied into the report.
const authErrorCode = (value) => (/^[a-z_]{1,64}$/.test(value ?? '') ? value : null);

// Auth0 sends the customer back here with a My Account API code. The token it buys is used for
// one operation and then dropped; nothing about it is stored.
function MyAccountCallback() {
  const { formatMessage: fm } = useIntl();
  const { AUTH0_DOMAIN: domain, OIDC_CLIENT_ID: clientId } = useEnv('root-first');
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const captureException = useCaptureException();
  const { deleteMfaMethod } = useDeleteMfaMethod();
  const [pending] = useState(readPendingOperation);
  const tokenRef = useRef(null);
  const passkeyEnrollmentRef = useRef(null);
  const startedRef = useRef(false);
  const [stage, setStage] = useState(STAGES.working);
  const [recoveryCode, setRecoveryCode] = useState(null);
  const [copied, setCopied] = useState(false);

  const returnTo = toSameOriginPath(pending?.returnTo, accountSettingsSecurity);

  const fail = (error, failedStage = STAGES.failed) => {
    tokenRef.current = null;
    captureException(error);
    setStage(failedStage);
  };

  const replaceRecoveryCode = async () => {
    // Auth0 lets a customer hold several codes but accepts only the oldest at sign-in, so the new
    // code is created first and works once every old one is gone. Until then the old one still works.
    const token = tokenRef.current;
    const { methodId: newId, recoveryCode: code } = await enrollRecoveryCode({ domain, token });
    // customers-api removes the codes it lists, which emails the customer and logs the removal.
    const listedIds = pending.replaceMethodIds ?? [];
    // A code left unconfirmed by an abandoned Universal Login enrollment is listed only by the My
    // Account API, so only that API can remove it.
    const hiddenIds = (await listAuthenticationMethods({ domain, token }))
      .filter(({ id, type }) => type === 'recovery-code' && id !== newId && !listedIds.includes(id))
      .map(({ id }) => id);
    let removedOne = false;
    // customers-api first, so a refused step-up comes before anything is removed. One at a time, so a
    // failure leaves the rest in place and is reported once.
    for (const methodId of listedIds) {
      // oxlint-disable-next-line no-await-in-loop
      const result = await deleteMfaMethod(methodId);
      // Nothing removed yet, so the old code still works. The customer goes through the forced MFA
      // login and back to the page they started from, to generate the code again.
      if (result?.errorCode === STEP_UP_REQUIRED && !removedOne) {
        tokenRef.current = null;
        navigate(generateMfaStepUpUrl(returnTo), { replace: true });
        return;
      }
      if (!result?.removed && result?.errorCode !== 'notFound') {
        const error = new Error(`Could not remove an old recovery code: ${result?.errorCode ?? 'no response'}`);
        // The new code is never shown: it works only once every old one is gone. If one already
        // went, the customer may have lost the code they saved, so they must hear it.
        fail(error, removedOne ? STAGES.recoveryCodeLost : STAGES.failed);
        return;
      }
      removedOne ||= result.removed;
    }
    for (const methodId of hiddenIds) {
      try {
        // oxlint-disable-next-line no-await-in-loop
        await deleteAuthenticationMethod({ domain, token, methodId });
        removedOne = true;
      } catch (error) {
        // Already gone is what this step wants.
        if (error?.status !== 404) {
          fail(error, removedOne ? STAGES.recoveryCodeLost : STAGES.failed);
          return;
        }
      }
    }
    tokenRef.current = null;
    clearMfaNudgeDecision();
    setRecoveryCode(code);
    setStage(STAGES.showRecoveryCode);
  };

  // Fetched before Continue shows, so the click can create the credential without waiting.
  const preparePasskey = async () => {
    passkeyEnrollmentRef.current = await startPasskeyEnrollment({ domain, token: tokenRef.current });
    setStage(STAGES.confirmPasskey);
  };

  useAsyncEffect(async () => {
    // A code can be exchanged once; a second run of this effect must not try again.
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;
    clearPendingOperation();

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const authError = authErrorCode(searchParams.get('error'));
    // The Action's own refusal, matched exactly so no other text from the link reaches the page.
    const needsTwoStep =
      authError === 'access_denied' && searchParams.get('error_description') === MY_ACCOUNT_NEEDS_TWO_STEP;
    // The code and state are single use; keep them out of history and anything copied from the bar.
    setSearchParams({}, { replace: true });

    // The sign-in was the whole step-up, so the code is never exchanged. Back on the page, the
    // removal is tried again and customers-api decides whether the sign-in was a passkey.
    if (pending?.operation === MY_ACCOUNT_OPERATIONS.signIn && pending.verifier && state === pending.state) {
      if (authError) {
        captureException(new Error(`Passkey sign-in step-up returned ${authError}`));
      }
      navigate(returnTo, { replace: true });
      return;
    }

    if (!pending?.verifier || !code || state !== pending.state) {
      fail(
        new Error(`My Account callback without a matching request: ${authError ?? 'no code'}`),
        needsTwoStep ? STAGES.needsTwoStep : STAGES.failed,
      );
      return;
    }

    try {
      tokenRef.current = await exchangeCode({ domain, clientId, code, verifier: pending.verifier });
      if (pending.operation === MY_ACCOUNT_OPERATIONS.passkey) {
        await preparePasskey();
      } else {
        await replaceRecoveryCode();
      }
    } catch (error) {
      fail(error);
    }
  }, []);

  // Runs on the click so the browser sees a user gesture before creating the credential.
  const handleAddPasskey = async () => {
    setStage(STAGES.working);
    try {
      await finishPasskeyEnrollment({ domain, token: tokenRef.current, enrollment: passkeyEnrollmentRef.current });
      tokenRef.current = null;
      clearMfaNudgeDecision();
      navigate(returnTo, { replace: true, state: { added: MY_ACCOUNT_OPERATIONS.passkey } });
    } catch (error) {
      // excludeCredentials matched: this device or password manager already holds one of theirs.
      if (error?.name === 'InvalidStateError') {
        tokenRef.current = null;
        setStage(STAGES.passkeyExists);
        return;
      }
      // The customer dismissed the browser prompt. The token still works, so Continue can try again
      // without another trip through Auth0, on a fresh challenge.
      if (error?.name === 'NotAllowedError') {
        try {
          await preparePasskey();
        } catch (prepareError) {
          fail(prepareError);
        }
        return;
      }
      fail(error);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCode);
      setCopied(true);
    } catch {
      // Clipboard denied or missing: the code stays on screen to copy by hand.
      setCopied(false);
    }
  };

  const backButton = (label) => (
    <Button
      clickstreamProps={{
        appSection: 'account',
        elementName: pending ? `my_account_${pending.operation.replace(/-/g, '_')}_back` : 'my_account_back',
      }}
      onClick={() => navigate(returnTo, { replace: true })}
      variant="contained"
      color="primary"
    >
      {label}
    </Button>
  );

  if (stage === STAGES.working) {
    return <PageLoading />;
  }

  if (MESSAGE_STAGES[stage]) {
    const [titleId, bodyId] = MESSAGE_STAGES[stage];
    return (
      <Styled.Card>
        <Heading component="h1" size="lg">
          {fm({ id: titleId })}
        </Heading>
        <Text component="p" size="md" color="secondary">
          {fm({ id: bodyId })}
        </Text>
        {backButton(fm({ id: LOCALIZED_TEXT.back }))}
      </Styled.Card>
    );
  }

  if (stage === STAGES.confirmPasskey) {
    return (
      <Styled.Card>
        <Heading component="h1" size="lg">
          {fm({ id: LOCALIZED_TEXT.passkeyTitle })}
        </Heading>
        <Text component="p" size="md" color="secondary">
          {fm({ id: LOCALIZED_TEXT.passkeyBody })}
        </Text>
        <Button
          clickstreamProps={{ appSection: 'account', elementName: 'my_account_passkey_continue' }}
          onClick={handleAddPasskey}
          variant="contained"
          color="primary"
        >
          {fm({ id: LOCALIZED_TEXT.passkeyContinue })}
        </Button>
      </Styled.Card>
    );
  }

  return (
    <Styled.Card>
      <Heading component="h1" size="lg">
        {fm({ id: LOCALIZED_TEXT.recoveryCodeTitle })}
      </Heading>
      <Text component="p" size="md" color="secondary">
        {fm({ id: LOCALIZED_TEXT.recoveryCodeBody })}
      </Text>
      <Styled.RecoveryCode component="p" size="lg" weight="medium" data-testid="recovery-code">
        {recoveryCode}
      </Styled.RecoveryCode>
      <Button
        clickstreamProps={{ appSection: 'account', elementName: 'my_account_recovery_code_copy' }}
        onClick={handleCopy}
        variant="outlined"
        color="primary"
      >
        {fm({ id: copied ? LOCALIZED_TEXT.copied : LOCALIZED_TEXT.copy })}
      </Button>
      {backButton(fm({ id: LOCALIZED_TEXT.done }))}
    </Styled.Card>
  );
}

export default MyAccountCallback;
